import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import type { SessionStatus } from '../../types/session.types'
import styles from './SessionTimer.module.css'

export interface SessionTimerProps {
  plannedStart: string // ISO8601
  plannedEnd: string   // ISO8601
  actualStart?: string
  status: SessionStatus
  className?: string
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':')
}

export function SessionTimer({
  plannedStart,
  plannedEnd,
  actualStart,
  status,
  className,
}: SessionTimerProps) {
  const [now, setNow] = useState<number>(() => Date.now())

  useEffect(() => {
    const timerFn = typeof window !== 'undefined' ? window : globalThis
    const interval = timerFn.setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => {
      timerFn.clearInterval(interval)
    }
  }, [])

  let timeDisplay = '00:00:00'
  let labelDisplay = 'ELAPSED'
  let stateClass = styles.normal

  const plannedStartMs = plannedStart ? new Date(plannedStart).getTime() : 0
  const plannedEndMs = plannedEnd ? new Date(plannedEnd).getTime() : 0
  const actualStartMs = actualStart ? new Date(actualStart).getTime() : plannedStartMs

  if (status === 'in_progress') {
    const elapsedSeconds = Math.max(0, Math.floor((now - actualStartMs) / 1000))
    timeDisplay = formatDuration(elapsedSeconds)
    const isOvertime = plannedEndMs > 0 && now > plannedEndMs

    if (isOvertime) {
      labelDisplay = 'OVERTIME'
      stateClass = styles.overtime
    } else {
      labelDisplay = 'ELAPSED'
      stateClass = styles.normal
    }
  } else if (status === 'draft' || status === 'ready') {
    const diffSeconds = Math.floor((plannedStartMs - now) / 1000)
    if (diffSeconds > 0) {
      timeDisplay = formatDuration(diffSeconds)
      labelDisplay = 'STARTS IN'
      stateClass = styles.countdown
    } else {
      timeDisplay = `+${formatDuration(Math.abs(diffSeconds))}`
      labelDisplay = 'START DELAYED'
      stateClass = styles.overtime
    }
  } else if (status === 'paused') {
    const elapsedSeconds = Math.max(0, Math.floor((now - actualStartMs) / 1000))
    timeDisplay = formatDuration(elapsedSeconds)
    labelDisplay = 'PAUSED'
    stateClass = styles.paused
  } else {
    // terminal: completed | failed | cancelled
    const totalDurationSeconds =
      plannedEndMs > actualStartMs
        ? Math.floor((plannedEndMs - actualStartMs) / 1000)
        : 0
    timeDisplay = formatDuration(totalDurationSeconds)
    labelDisplay = 'TOTAL DURATION'
    stateClass = status === 'failed' ? styles.overtime : styles.normal
  }

  return (
    <div className={clsx(styles.container, className)} role="timer" aria-label={labelDisplay}>
      <span className={clsx(styles.timer, stateClass)}>{timeDisplay}</span>
      <span className={styles.label}>{labelDisplay}</span>
    </div>
  )
}
